import { EditOutlined, FilterOutlined, InfoCircleOutlined } from '@ant-design/icons';
import { Space, Table, Tag, Button, Tooltip } from 'antd';
import { PermissionContext } from 'components/Global/AuthLayout';
import { EmployeeTableInterface } from 'interfaces/employee.interface';
import { useContext, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useSearchParams } from 'react-router-dom';
import {
	ActiveStatusSvg,
	DeceasedSvg,
	DeleteActionSvg,
	DismissedSvg,
	ReassignedSvg,
	RedundantSvg,
	RetiredSvg,
	SuspendedSvg,
} from 'utils/svgs';
import {
	capitalizeFirstLetter,
	formatDate,
	invalidText,
	toastText,
} from 'utils/utils';
import styles from './index.module.scss';
import { getAccessibleBranchForUser } from 'Api/masters/branch-code';

const { Column } = Table;

type Props = {
	employeeData: EmployeeTableInterface[];
	totalRecords: number;
	currentPage: number;
	pageSize: number;
	tableChangeHandler: (pagination: any, filter: any, sorter: any) => void;
	fetchRecords: any;
	isLoading: boolean;
	setIsAddEmployeeModalOpen: (value: boolean) => void;
	setViewOnlyEmployeeDetails: (value: boolean) => void;
	showModal: any;
	setDeleteEmployee: any;
	branchFilterValue: string[]; // Add this prop
};
interface DropdownOption {
	label: string;
	value: string;
}

const EmployeeTable = (props: Props) => {
	const {
		employeeData,
		totalRecords,
		currentPage,
		pageSize,
		tableChangeHandler,
		isLoading,
		setIsAddEmployeeModalOpen,
		setViewOnlyEmployeeDetails,
		showModal,
		setDeleteEmployee,
		branchFilterValue,
	} = props;
	const [searchParams, setSearchParams] = useSearchParams();
	const [accessibleBranches, setAccessibleBranches] = useState<
		DropdownOption[]
	>([]);
	const [isBranchesLoading, setIsBranchesLoading] = useState<boolean>(false);
	const permissionContext = useContext(PermissionContext);

	const { data: profileData } = useSelector(
		(state: any) => state?.userProfile
	);

	const handleEditEmployee = (id: string) => {
		setIsAddEmployeeModalOpen(true);
		setSearchParams({
			employeeId: id,
		});
	};

	// Delete user data handler
	const deleteDataHandler = (id: any) => {
		setDeleteEmployee(id);

		showModal();
	};

	const handleViewEmployee = (id: string) => {
		setViewOnlyEmployeeDetails(true);
		setSearchParams({
			employeeId: id,
			onlyView: 'true',
		});
	};

	const permissionsToCheck = [
		'Edit_Employment',
		'Edit_Salary',
		'Edit_Payment_Info',
		'Edit_Taxation',
		'Edit_Deductions',
		'Edit_Earnings',
	];

	const deletePermissions = [
		'Delete_Employment',
		'Delete_Salary',
		'Delete_Payment_Info',
		'Delete_Taxation',
		'Delete_Deductions',
		'Delete_Earnings',
	];

	const isEdit = permissionContext.allowedPermissions.some(
		(permission: string) => permissionsToCheck.includes(permission)
	);

	const isDelete = permissionContext.allowedPermissions.some(
		(permission: string) => deletePermissions.includes(permission)
	);

	const fetchAccessibleBranches = async () => {
		try {
			setIsBranchesLoading(true);

			const response = await getAccessibleBranchForUser();

			const branches: DropdownOption[] = response?.data?.data ?? [];

			branches.shift();
			setAccessibleBranches(branches);
		} catch (error: any) {
			const message =
				error?.response?.data?.message ||
				'Failed to fetch accessible branches';
			toastText(message, 'error');
		} finally {
			setIsBranchesLoading(false);
		}
	};

	useEffect(() => {
		fetchAccessibleBranches();
	}, []);

	return (
		<div className={styles['dynamic-table']}>
			<Table
				dataSource={employeeData}
				scroll={{ x: 'max-content', y: 'calc(100vh - 340px)' }}
				pagination={{
					total: totalRecords,
					current: currentPage,
					pageSize: pageSize,
					showSizeChanger: false,
					// onShowSizeChange: pageSizeHandler,
					pageSizeOptions: [30, 60, 90, 120, 200],
					showTotal: (total) => (
						<span
							style={{
								marginLeft: '15px',
								fontSize: '15px',
							}}
						>
							Total No. of Employees : {total}
						</span>
					),
				}}
				onChange={tableChangeHandler}
				rowKey={(record) => record.id}
				loading={isLoading}
			>
				<Column
					title="Employee Name"
					dataIndex="firstName"
					key="firstName"
					className="bg-white"
					width={180}
					fixed="left"
					render={(text, record: any) => {
						const fullName = [
							record.firstName,
							record.midName,
							record.lastName,
						]
							.filter(Boolean)
							.join(' ');

						return (
							<span
								style={{ cursor: 'pointer', color: 'blue' }}
								onClick={() => handleViewEmployee(record.id)}
							>
								{fullName}
							</span>
						);
					}}
				/>

				<Column
					title="Emp. Code | Reference No."
					dataIndex="employeeId"
					key="employeeId"
					className="bg-white"
					width={250}
					fixed="left"
					sorter={true}
					render={(value: any, record: any) => {
						const { employeeId, referenceNumber } = record;
						const displayText = referenceNumber
							? `${employeeId} | ${referenceNumber}`
							: employeeId;

						return (
							<div
								style={{
									display: 'flex',
									alignItems: 'center',
								}}
							>
								<span>{displayText}</span>
								{referenceNumber && (
									<Tooltip
										title="External System Unique Identifier"
										placement="top"
									>
										<InfoCircleOutlined
											style={{
												marginLeft: 6,
												color: '#584495',
											}}
										/>
									</Tooltip>
								)}
							</div>
						);
					}}
				/>
				<Column
					title="Joining Date"
					dataIndex="joiningDate"
					key="joiningDate"
					className="bg-white"
					width={150}
					render={(value) => {
						return formatDate(
							value,
							profileData?.companyDateFormat ?? 'DD/MM/YYYY'
						);
					}}
					sorter={true}
				/>
				<Column
					title="Currency"
					dataIndex="currency"
					className="bg-white"
					key="currency"
					width={100}
					render={(value: any, record: any) => {
						return record.currency;
					}}
				/>
				<Column
					title="Gender"
					dataIndex="gender"
					className="bg-white"
					key="gender"
					width={100}
					sorter={true}
					render={(value: any) => {
						return capitalizeFirstLetter(value);
					}}
				/>
				<Column
					title="Branch"
					dataIndex="branchName"
					className="bg-white"
					key="branchName"
					width={150}
					sorter={false}
					filters={accessibleBranches.map((branch) => ({
						text: branch.label,
						value: branch.value,
					}))}
					filterSearch={true}
					filterMultiple={true} // Add this for multi-select
					filteredValue={
						branchFilterValue.length > 0 ? branchFilterValue : null
					} // Update this
					filterIcon={(
						filtered // Add this for visual feedback
					) => (
						<FilterOutlined
							style={{
								fontSize: '16px',
								color:
									branchFilterValue.length > 0
										? '#584495'
										: '#ccc',
								transition: 'color 0.2s',
							}}
						/>
					)}
				/>
				<Column
					title="Status"
					className="bg-white"
					dataIndex="recordStatus"
					key="recordStatus"
					width={120}
					render={(value) => {
						let statusText = '';
						let statusClass = '';
						let StatusIcon = null;
						switch (value) {
							case 'ACTIVE':
								statusText = 'Active';
								statusClass = styles['status-active'];
								StatusIcon = <ActiveStatusSvg />;
								break;
							case 'SUSPENDED':
								statusText = 'Suspended';
								statusClass = styles['status-suspended'];
								StatusIcon = <SuspendedSvg />;
								break;
							case 'RETIRED':
								statusText = 'Retired';
								statusClass = styles['status-retired'];
								StatusIcon = <RetiredSvg />;
								break;
							case 'REASSIGNED':
								statusText = 'Reassigned';
								statusClass = styles['status-reassigned'];
								StatusIcon = <ReassignedSvg />;
								break;
							case 'DISMISSED':
								statusText = 'Dismissed';
								statusClass = styles['status-dismissed'];
								StatusIcon = <DismissedSvg />;
								break;
							case 'DECEASED':
								statusText = 'Deceased';
								statusClass = styles['status-deceased'];
								StatusIcon = <DeceasedSvg />;
								break;
							case 'REDUNDANT':
								statusText = 'Redundant';
								statusClass = styles['status-redundant'];
								StatusIcon = <RedundantSvg />;
								break;
							default:
								statusText = 'Unknown Status';
								statusClass = styles['status-unknown'];
								StatusIcon = null;
						}
						return (
							<Tag
								bordered={false}
								className={`${styles['statusTag']} ${statusClass}`}
							>
								<div className={styles['statusIcon']}>
									{StatusIcon}
									<span>{statusText}</span>
								</div>
							</Tag>
						);
					}}
				/>

				{(isEdit || isDelete) && (
					<Column
						title="Action"
						className="bg-white"
						key="action"
						width={200}
						render={(_: any, record: any) => (
							<Space size="small" wrap>
								{isEdit && (
									<p className="cursor-pointer">
										<EditOutlined
											style={{
												fontSize: 18,
											}}
											onClick={() => {
												handleEditEmployee(record.id);
											}}
										/>
									</p>
								)}
								{isDelete && (
									<p
										className="cursor-pointer"
										onClick={() =>
											deleteDataHandler(record.id)
										}
									>
										<DeleteActionSvg />
									</p>
								)}
							</Space>
						)}
					/>
				)}
			</Table>
		</div>
	);
};

export default EmployeeTable;
